/**
 * Spec 004 US1 (T007) — Client-side closed-quartals lookup.
 *
 * Returns a `Set<string>` keyed by ``${year}-Q${quarter}`` containing
 * every quartal that is currently locked for the active tenant.
 * Reuses the existing TanStack Query cache (`['quartal-status']`)
 * already populated by `<QuartalStatusBanner>`, so zero extra fetch.
 *
 * Per research.md R3 / data-model.md § Client-side derivation, a
 * quartal is considered closed iff `closedAt IS NOT NULL AND
 * reopenedAt IS NULL`. We accept both the explicit `status === 'closed'`
 * flag (when the backend has projected it) and the underlying timestamp
 * pair, so the helper survives a future projection cleanup.
 *
 * The pure derivation lives in `closedQuartalsSetFromRows()` so unit
 * tests can cover it without spinning up React.
 */
import { useMemo } from 'react';
import { useQuartals, type QuartalRow } from '../services/api/quartal';

export function closedQuartalsSetFromRows(
  rows: ReadonlyArray<QuartalRow> | undefined,
): Set<string> {
  if (!rows || rows.length === 0) return new Set();
  const out = new Set<string>();
  for (const q of rows) {
    const closedFlag =
      q.status === 'closed' || (q.closedAt !== null && q.reopenedAt === null);
    if (!closedFlag) continue;
    out.add(`${q.year}-Q${q.quarter}`);
  }
  return out;
}

/**
 * Lookup hook — call once at the top of a list view, then consult
 * `.has(\`${year}-Q${quarter}\`)` per row. O(1) per check.
 */
export function useClosedQuartalsSet(year?: number): Set<string> {
  const { data } = useQuartals(year);
  return useMemo(() => closedQuartalsSetFromRows(data?.items), [data]);
}
